import * as XLSX from "xlsx";
import { normalizeString } from "../utils/normalizeString";
import actionPlansService from "./actionPlansService";
import benchmarkService from "./benchmarkService";
import driverEssenceService from "./driverEssenceService";
import driverMetricService from "./driverMetricService";
import driverService from "./driverService";
import journeyService from "./journeyService";
import overviewmetricService from "./overviewmetricService";
import respondentsMonthlyService from "./respondentsMonthlyService";
import segmentationService from "./segmentationService";
import {
  buildParams,
  buildRespondentsMonthlyParams,
} from "./dashboardService";

const PAGE_SIZE = 100;

/**
 * Os serviços do dashboard não passam pageSize (o backend usa o default de
 * 20 registros por página) — pra um export de "todos os dados" isso
 * truncaria silenciosamente qualquer domínio com mais de 20 linhas. Aqui
 * paginamos explicitamente até `pagination.hasNext` acabar.
 */
async function fetchAllPages(getFn, baseParams = {}) {
  let page = 1;
  let all = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await getFn({ ...baseParams, page, pageSize: PAGE_SIZE });
    const rows = Array.isArray(res?.data) ? res.data : [];
    all = all.concat(rows);
    if (!res?.pagination?.hasNext) break;
    page += 1;
  }
  return all;
}

const num = (v) => (v === null || v === undefined ? "" : Number(v));
const str = (v) => (v === null || v === undefined ? "" : String(v));

function mapOverview(rows) {
  return rows.map((r) => ({
    Período: str(r.period_name),
    NPS: num(r.nps),
    "Meta NPS": num(r.nps_target),
    CSAT: num(r.csat),
    "Meta CSAT": num(r.csat_target),
    CES: num(r.ces),
    "Meta CES": num(r.ces_target),
    "Jornada CX": num(r.cx),
    "Meta Jornada CX": num(r.cx_target),
  }));
}

function mapJourney(rows) {
  return rows.map((r) => ({
    Etapa: str(r.name),
    Valor: num(r.value),
    Variação: num(r.value_delta),
    "Sistema Unimed": num(r.unimed_system),
    Meta: num(r.target),
    Categoria: str(r.category),
    "Opção 1": num(r.opcao_1),
    "Opção 2": num(r.opcao_2),
    "Opção 3": num(r.opcao_3),
    "Opção 4": num(r.opcao_4),
    "Opção 5": num(r.opcao_5),
  }));
}

function mapDrivers(rows) {
  return rows.map((r) => ({
    Driver: str(r.name),
    Essência: str(r.essence),
    Menções: num(r.mentions_count),
    Positivas: num(r.positive_count),
    Neutras: num(r.neutral_count),
    Negativas: num(r.negative_count),
  }));
}

function mapDriverHistory(rows) {
  return rows.map((r) => ({
    Driver: str(r.driver_name),
    Período: str(r.period_name),
    Menções: num(r.total_mentions),
    Positivas: num(r.total_positive),
    Negativas: num(r.total_negative),
  }));
}

function mapDriverEssence(rows) {
  return rows.map((r) => ({
    Período: str(r.period_name),
    Essência: str(r.essence),
    Positivas: num(r.positive),
    Neutras: num(r.neutral),
    Negativas: num(r.negative),
  }));
}

function mapBenchmarks(rows) {
  return rows.map((r) => ({
    Indicador: str(r.name),
    Categoria: str(r.category),
    Valor: num(r.value),
    "Média Sistema Unimed": num(r.unimed_system_avg),
    "Concorrência Nacional": num(r.national_competition),
  }));
}

function mapSegmentation(rows, withPeriod) {
  return rows.map((r) => ({
    Descrição: str(r.description),
    Tipo: str(r.type_description),
    Categoria: str(r.category),
    Valor: num(r.value),
    Variação: num(r.value_delta),
    Menções: num(r.mentions_count),
    ...(withPeriod ? { Período: str(r.period_name) } : {}),
  }));
}

function mapRespondentsMonthly(rows) {
  return rows.map((r) => ({
    "Tipo de Período": str(r.period_type),
    "Nome do Período": str(r.period_name),
    "Ano-Mês": str(r.year_month),
    Início: str(r.period_start_date),
    "Total de Respondentes": num(r.total_respondents),
  }));
}

function mapActionPlans(rows) {
  return rows.map((r) => ({
    Título: str(r.title),
    Descrição: str(r.description),
    Indicador: str(r.indicator),
    "Valor Atual": num(r.current_value),
    Meta: num(r.target_value),
    Responsável: str(r.responsible),
    Prazo: str(r.due_date),
    Prioridade: str(r.priority),
    Status: str(r.status),
    "Criado em": str(r.created_at),
  }));
}

function appendSheet(wb, rows, name) {
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
  XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
}

export async function buildWorkbook(filters) {
  const params = buildParams(filters);

  const [
    overviewHistory,
    journey,
    drivers,
    driverHistory,
    driverEssence,
    benchmarks,
    segmentation,
    segmentationHistory,
    respondentsMonthly,
    actionPlans,
  ] = await Promise.all([
    fetchAllPages(overviewmetricService.get, {}),
    fetchAllPages(journeyService.get, params),
    fetchAllPages(driverService.get, params),
    fetchAllPages(driverMetricService.get, {}),
    fetchAllPages(driverEssenceService.get, {}),
    fetchAllPages(benchmarkService.get, params),
    fetchAllPages(segmentationService.get, params),
    fetchAllPages(segmentationService.get, {}),
    fetchAllPages(
      respondentsMonthlyService.get,
      buildRespondentsMonthlyParams(filters),
    ),
    fetchAllPages(actionPlansService.get, {}),
  ]);

  const wb = XLSX.utils.book_new();
  appendSheet(wb, mapOverview(overviewHistory), "Visão Geral");
  appendSheet(wb, mapJourney(journey), "Jornada");
  appendSheet(wb, mapDrivers(drivers), "Drivers");
  appendSheet(wb, mapDriverHistory(driverHistory), "Drivers - Histórico Mensal");
  appendSheet(wb, mapDriverEssence(driverEssence), "Drivers - Essência Mensal");
  appendSheet(wb, mapBenchmarks(benchmarks), "Benchmarks");
  appendSheet(wb, mapSegmentation(segmentation, false), "Segmentação");
  appendSheet(
    wb,
    mapSegmentation(segmentationHistory, true),
    "Segmentação - Histórico",
  );
  appendSheet(
    wb,
    mapRespondentsMonthly(respondentsMonthly),
    "Respondentes por Mês",
  );
  appendSheet(wb, mapActionPlans(actionPlans), "Plano de Ação");

  return wb;
}

export async function exportDashboardToXlsx(filters, companyName) {
  const wb = await buildWorkbook(filters);
  const safeCompany = normalizeString(companyName || "empresa").replace(
    /[^a-zA-Z0-9]+/g,
    "_",
  );
  const period = filters?.period || "geral";
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `121insights_${safeCompany}_${period}_${today}.xlsx`);
}
